import { Server } from 'mock-socket';
import configJson from './config.json';
import configNclJson from './config-ncl.json';
import { isOfflineNCLMode } from './utils';
import {
    buildConversationMap,
    setConversationMap,
    resetActiveReplayId,
    socketSessions,
    handleUserMessage
} from './aiChatSeeder';

const offlineConfig = isOfflineNCLMode() ? configNclJson : configJson;

// ============================================================================
// Server Lifecycle
// ============================================================================

window.aiChatServerInstance = null;

/**
 * Initializes the AI Chat WebSocket server.
 * Builds the conversation map from all chat JSON files under data/chat/,
 * then sets up a mock WebSocket server using the endpoint path from chatConstants.js.
 * On receiving a user prompt, it matches it to the closest recorded conversation
 * and replays the corresponding server responses, supporting multi-turn dialogs.
 * @returns {void}
 */
const initAiChatServer = () => {
    // Build conversation map from all chat recordings
    setConversationMap(buildConversationMap());

    const { AI_CHAT_ENDPOINT_PATH } = offlineConfig.aiChat;
    const baseUrl = process.env.REACT_APP_OCCM_ENDPOINT || window.location.origin;
    const protocol = baseUrl.startsWith('https') ? 'wss:' : 'ws:';
    const host = baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const wsUrl = `${protocol}//${host}${AI_CHAT_ENDPOINT_PATH}`;

    console.log('AI Chat WebSocket server URL:', wsUrl);
    window.aiChatServerInstance = new Server(wsUrl);

    window.aiChatServerInstance.on('connection', socket => {
        console.log('AI Chat: WebSocket connection established');

        socket.on('message', data => {
            console.log('AI Chat: Received client message:', data);

            try {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data;

                // Handle ping messages with pong response
                if (parsed.type === 'ping') {
                    socket.send(JSON.stringify({ type: 'pong' }));
                    return;
                }

                // If client sends a chat message (has messages array), handle the turn
                if (parsed.messages && Array.isArray(parsed.messages)) {
                    const userMessages = parsed.messages.filter(m => m.role === 'user');
                    const lastUserMsg = userMessages[userMessages.length - 1];
                    const userContent = lastUserMsg?.content || '';
                    console.log(`AI Chat: User prompt received — "${userContent.substring(0, 60)}"`);

                    handleUserMessage(socket, userContent);
                }
            } catch (err) {
                console.log('AI Chat: Non-JSON message received:', data);
            }
        });

        socket.on('error', error => {
            console.log('AI Chat: WebSocket error:', error);
        });

        socket.on('close', () => {
            console.log('AI Chat: WebSocket connection closed');
            socketSessions.delete(socket);
        });
    });

    window.aiChatServerInstance.on('error', error => {
        console.log('AI Chat WebSocket server error:', error);
    });

    console.log('AI Chat WebSocket server initialized at:', wsUrl);
};

/**
 * Shuts down the AI Chat WebSocket server if it is running.
 * @returns {void}
 */
const shutdownAiChatServer = () => {
    if (window.aiChatServerInstance) {
        console.log('Shutting down AI Chat WebSocket server');
        window.aiChatServerInstance.close();
        window.aiChatServerInstance = null;
        setConversationMap(null);
        resetActiveReplayId();
        socketSessions.clear();
        console.log('AI Chat WebSocket server shut down');
    }
};

export { initAiChatServer, shutdownAiChatServer };
